import { AlertCircle, Clock, Package, Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { getCustomerPackages } from '../../../api/package.api';
import styles from '../../../assets/scss/pages/CustomerPackages.module.scss';
import CustomerPackageDetailModal from './CustomerPackageDetailModal';

// Port từ app: screens/admin/CustomerPackagesScreen.js (chuyển sang panel trong CustomerDetail).
// List thẻ gói của 1 khách: status badge + số buổi + giá thực bán + NV bán + ngày mua.
// Click card → mở CustomerPackageDetailModal.

const STATUS_META = {
    active: { label: 'Đang dùng', color: '#16a34a', bg: '#dcfce7' },
    frozen: { label: 'Bảo lưu', color: '#0891b2', bg: '#cffafe' },
    completed: { label: 'Hoàn tất', color: '#64748b', bg: '#f1f5f9' },
    expired: { label: 'Hết hạn', color: '#dc2626', bg: '#fee2e2' },
    refunded: { label: 'Đã hoàn', color: '#9333ea', bg: '#f3e8ff' },
    cancelled: { label: 'Đã huỷ', color: '#6b7280', bg: '#f3f4f6' },
};

const formatVnd = (n) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

export default function CustomerPackagesPanel({ customerId }) {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detailId, setDetailId] = useState(null);
    const [imgErrorIds, setImgErrorIds] = useState(() => new Set());

    const fetchPackages = useCallback(async () => {
        if (!customerId) {
            setError('Thiếu thông tin khách hàng');
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const res = await getCustomerPackages(customerId);
            const data = res?.data?.data || res?.data || [];
            setPackages(Array.isArray(data) ? data : []);
            setError('');
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Không tải được danh sách thẻ');
            setPackages([]);
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        fetchPackages();
    }, [fetchPackages]);

    const handleImgError = (id) =>
        setImgErrorIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });

    if (loading) {
        return (
            <div className={styles.center}>
                <div className={styles.spinner} />
                <span className={styles.muted}>Đang tải...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.center}>
                <AlertCircle size={48} color="#ef4444" />
                <span className={styles.muted} style={{ color: '#ef4444' }}>{error}</span>
                <button className={styles.retryBtn} onClick={fetchPackages}>
                    Thử lại
                </button>
            </div>
        );
    }

    if (packages.length === 0) {
        return (
            <div className={styles.center}>
                <Package size={56} color="#d1d5db" />
                <span className={styles.emptyTitle}>Chưa có thẻ nào</span>
                <span className={styles.muted}>Khách chưa mua gói dịch vụ</span>
            </div>
        );
    }

    return (
        <>
            <div className={styles.panel}>
                {packages.map((item) => {
                    const snap = item.templateSnapshot || {};
                    const name = snap.name || item.package?.name || `Thẻ ${item.code}`;
                    const urlImg = snap.urlImg || item.package?.urlImg;
                    const sellerName = item.sellerEmployee?.name || '—';
                    const total = Number(item.totalSessions || 0);
                    const remaining = Math.max(0, total - Number(item.usedSessions || 0));
                    const statusMeta = STATUS_META[item.status] || STATUS_META.active;
                    const showImg = urlImg && !imgErrorIds.has(item.id);

                    return (
                        <div key={item.id} className={styles.card} onClick={() => setDetailId(item.id)}>
                            <div className={styles.cardHeader}>
                                {showImg ? (
                                    <img
                                        className={styles.thumb}
                                        src={urlImg}
                                        alt={name}
                                        onError={() => handleImgError(item.id)}
                                    />
                                ) : (
                                    <span className={styles.thumbFallback}>
                                        <Package size={26} />
                                    </span>
                                )}
                                <div className={styles.cardHeadInfo}>
                                    <div className={styles.titleRow}>
                                        <h4 className={styles.name}>{name}</h4>
                                        <span
                                            className={styles.statusBadge}
                                            style={{ background: statusMeta.bg, color: statusMeta.color }}
                                        >
                                            {statusMeta.label}
                                        </span>
                                    </div>
                                    <div className={styles.code}>Mã thẻ: {item.code}</div>
                                </div>
                            </div>

                            <div className={styles.divider} />

                            <div className={styles.infoGrid}>
                                <div className={styles.infoCell}>
                                    <div className={styles.infoLabel}>Số buổi</div>
                                    <div className={styles.infoValue}>
                                        {Number(item.usedSessions || 0)}/{total}
                                        {Number(item.bonusSessions || 0) > 0
                                            ? ` (+${item.bonusSessions} tặng)`
                                            : ''}
                                    </div>
                                    <div className={styles.infoSub}>Còn {remaining} buổi</div>
                                </div>
                                <div className={styles.infoCell}>
                                    <div className={styles.infoLabel}>HSD</div>
                                    <div className={styles.infoValue}>
                                        {item.expiredAt ? formatDate(item.expiredAt) : 'Vô hạn'}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.infoGrid}>
                                <div className={styles.infoCell}>
                                    <div className={styles.infoLabel}>Giá thực bán</div>
                                    <div className={styles.infoValue} style={{ color: '#f45a31' }}>
                                        {formatVnd(item.totalContractValue)}
                                    </div>
                                </div>
                                <div className={styles.infoCell}>
                                    <div className={styles.infoLabel}>NV bán</div>
                                    <div className={styles.infoValue}>{sellerName}</div>
                                </div>
                            </div>

                            <div className={styles.metaRow}>
                                <Clock size={14} />
                                <span>Mua: {formatDate(item.purchasedAt)}</span>
                                {item.isOverridden && (
                                    <>
                                        <span className={styles.metaDot} />
                                        <Pencil size={14} color="#f59e0b" />
                                        <span style={{ color: '#f59e0b' }}>Đã chỉnh</span>
                                    </>
                                )}
                            </div>

                            {item.note ? (
                                <div className={styles.noteBox}>
                                    <div className={styles.noteLabel}>Ghi chú khách:</div>
                                    <div className={styles.noteText}>{item.note}</div>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <CustomerPackageDetailModal
                open={!!detailId}
                packageId={detailId}
                onClose={() => setDetailId(null)}
            />
        </>
    );
}
